import { Provider } from '@angular/core';
import { DateAdapter } from './adapters/abstract-date-adapter';
import { NativeDateAdapter } from './adapters/native-date-adapter';
import { DateFormats, HX_DATE_ADAPTER, HX_DATE_FORMATS, HX_DATE_LOCALE } from './date-picker-tokens';

/** Configuration options for DatePicker providers. */
export type DatePickerConfig = {
  /** Locale code (e.g., 'en-US', 'fr-FR', 'de-DE'). Defaults to 'en-US' if not provided. */
  locale?: string;

  /** Date format patterns for parsing user input and displaying dates. If not provided, defaults to MM/DD/YYYY patterns. */
  formats?: DateFormats;

  /**
   * Custom DateAdapter implementation. If not provided, defaults to NativeDateAdapter (JavaScript Date).
   *
   * @example
   *   ```typescript
   *   import { LuxonAdapter } from '@complexcare/harmony';
   *
   *   provideDatePickerConfig({
   *     adapter: LuxonAdapter
   *   })
   *   ```;
   */
  adapter?: new () => DateAdapter<any>;
};

/**
 * Provides DatePicker configuration for the application. Use in root or feature-level providers.
 *
 * @example
 *   ```typescript
 *   // app.config.ts
 *   import { provideDatePickerConfig } from '@complexcare/harmony';
 *
 *   export const appConfig: ApplicationConfig = {
 *     providers: [
 *       provideDatePickerConfig({
 *         locale: 'fr-FR',
 *         formats: {
 *           parse: ['DD/MM/YYYY', 'D/M/YY'],
 *           display: 'DD/MM/YYYY'
 *         }
 *       })
 *     ]
 *   };
 *   ```;
 *
 * @example
 *   ```typescript
 *   // With Luxon adapter
 *   import { provideDatePickerConfig, LuxonAdapter } from '@complexcare/harmony';
 *
 *   export const appConfig: ApplicationConfig = {
 *     providers: [
 *       provideDatePickerConfig({
 *         locale: 'en-US',
 *         adapter: LuxonAdapter
 *       })
 *     ]
 *   };
 *   ```;
 *
 * @param config Optional configuration for locale, formats, and adapter
 */
export function provideDatePickerConfig(config?: DatePickerConfig): Provider[] {
  const providers: Provider[] = [];

  // Provide locale if specified
  if (config?.locale) {
    providers.push({
      provide: HX_DATE_LOCALE,
      useValue: config.locale,
    });
  }

  // Provide formats if specified
  if (config?.formats) {
    providers.push({
      provide: HX_DATE_FORMATS,
      useValue: config.formats,
    });
  }

  // Provide adapter (default to NativeDateAdapter if not specified)
  const adapterClass = config?.adapter ?? NativeDateAdapter;
  providers.push({
    provide: HX_DATE_ADAPTER,
    useClass: adapterClass,
  });

  return providers;
}

/**
 * Provides default DatePicker configuration with NativeDateAdapter. Convenience function equivalent to
 * `provideDatePickerConfig()` with no arguments.
 *
 * @example
 *   ```typescript
 *   // app.config.ts
 *   import { provideNativeDateAdapter } from '@complexcare/harmony';
 *
 *   export const appConfig: ApplicationConfig = {
 *     providers: [
 *       provideNativeDateAdapter()
 *     ]
 *   };
 *   ```;
 */
export function provideNativeDateAdapter(): Provider[] {
  return provideDatePickerConfig();
}
